import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Globe } from "lucide-react";
import AskVytali from "../components/AskVytali";

const STAGES = ["AMBASSADEUR", "NEW_START", "COMMERCIAL", "LEADER", "TEAM_LEADER", "CORPORATE_TRAINER", "ASSISTANT_MANAGER", "MANAGER"];

export default function Home() {
  const { t, i18n } = useTranslation();
  const toggleLang = () => {
    const next = i18n.language === "fr" ? "en" : "fr";
    i18n.changeLanguage(next);
    localStorage.setItem("vytali_lang", next);
  };
  const schools = [t("home.s1"), t("home.s2"), t("home.s3"), t("home.s4"), t("home.s5")];
  const principles = [t("home.p1"), t("home.p2"), t("home.p3"), t("home.p4"), t("home.p5")];

  return (
    <div className="min-h-screen bg-[#0A1F44] text-white" data-testid="public-homepage">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5 font-heading font-semibold text-lg">
          <img src="/brand/emblem.svg" alt="Vytali Academy" className="h-10 w-auto" />
          <span className="hidden sm:inline">Vytali Academy</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleLang} data-testid="home-lang-toggle"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono-v font-semibold border border-white/25 rounded-md hover:bg-white/10 transition-colors duration-200 uppercase">
            <Globe size={13} /> {i18n.language === "fr" ? "FR" : "EN"}
          </button>
          <Link to="/register" data-testid="home-signup-cta"
            className="px-4 py-2 text-sm font-medium border border-[#B99341] text-[#D4B45A] rounded-md hover:bg-[#B99341]/10 transition-colors duration-200">
            {t("login.signUp")}
          </Link>
          <Link to="/login" data-testid="home-login-cta"
            className="px-4 py-2 text-sm font-medium bg-[#B99341] text-[#0A1F44] rounded-md hover:bg-[#D4B45A] transition-colors duration-200">
            {t("login.submit")}
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-20 text-center">
        <img src="/brand/emblem.svg" alt="" className="h-36 sm:h-44 w-auto mx-auto fade-up" />
        <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[0.12em] fade-up-1">VYTALI</h1>
        <p className="mt-1 text-lg sm:text-xl tracking-[0.5em] text-[#D4B45A] fade-up-1">ACADEMY</p>
        <div className="mx-auto mt-6 w-24 h-px bg-[#B99341]" />
        <p className="mt-6 text-base md:text-lg text-white/80 italic max-w-2xl mx-auto fade-up-2">{t("home.tagline")}</p>
        <p className="mt-4 text-xs tracking-[0.4em] text-[#D4B45A] fade-up-2">DEPUIS 2003</p>
        <Link to="/login" data-testid="home-hero-cta"
          className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 bg-[#B99341] text-[#0A1F44] font-medium rounded-md hover:bg-[#D4B45A] transition-colors duration-200 fade-up-3">
          {t("home.cta")} <ArrowRight size={16} />
        </Link>
      </section>

      <section className="bg-white text-[#0A1F44]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#B99341]">{t("home.principleTitle")}</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            {principles.map((p, i) => (
              <div key={i} className="border-t-2 border-[#B99341] pt-4">
                <span className="font-mono-v text-xs text-[#B99341]">0{i + 1}</span>
                <p className="mt-2 text-sm leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F4F2ED] text-[#0A1F44]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#B99341]">{t("home.schoolsTitle")}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {schools.map((s, i) => (
              <div key={i} className="bg-white border border-[#0A1F44]/10 rounded-md p-5" data-testid={`home-school-${i + 1}`}>
                <img src="/brand/emblem.svg" alt="" className="h-8 w-auto opacity-90" />
                <p className="mt-3 font-heading font-semibold text-sm leading-snug">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4B45A]">{t("home.journeyTitle")}</p>
        <div className="mt-6 overflow-x-auto">
          <div className="flex items-center min-w-[760px]">
            {STAGES.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <div className="h-px flex-1 min-w-4 bg-[#B99341]/50" />}
                <div className="flex flex-col items-center gap-2 px-1">
                  <div className="w-8 h-8 rounded-md border border-[#B99341] text-[#D4B45A] flex items-center justify-center text-xs font-mono-v">{i + 1}</div>
                  <span className="text-[9px] font-mono-v text-white/70 text-center max-w-20">{s.replace(/_/g, " ")}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <p className="mt-6 text-sm text-white/60 italic">{t("journey.humanDecision")}</p>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <span className="flex items-center gap-2">
            <img src="/brand/emblem.svg" alt="" className="h-6 w-auto" /> Vytali Academy — {t("login.subtitle")}
          </span>
          <span className="font-mono-v tracking-widest text-[#B99341]/80">DEPUIS 2003 · vytaliacademy.com</span>
        </div>
      </footer>
      <AskVytali page="/home-public" />
    </div>
  );
}
